import { Component, inject, Input, OnDestroy, OnInit, signal, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { IProject } from '@static/models/project';
import { ActivitiesService } from '@shared/services/activities.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ProjectService } from '@shared/services/project.service';
import { MatCardModule } from '@angular/material/card';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { AuthService } from '@static/services/auth.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StorageService } from '@static/services/storage.service';
import { KEY_PROJECT_CURRENT_TAB } from '@static/storage-keys';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkService } from '@extensions/assignment/services/work.service';
import { WbsFilterService } from '@shared/services/wbs-filter.service';
import { ContractorListComponent } from '@extensions/assignment/tabs/contractor-list/contractor-list.component';
import { TaskListComponent } from './task-list/task-list.component';
import { ReviewComponent } from '@extensions/review/tabs/review.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ProjectPermissionsComponent } from './project-permissions/project-permissions.component';
import { ObservableService } from '@static/services/observable.service';
import { MatMenuModule } from '@angular/material/menu';
import { LockComponent } from '@static/components/lock/lock.component';
import { EPermissionLevel } from '@static/models/permissions';
import { ExtensionService } from '@shared/services/extension.service';
import { ExpenseSignoffComponent } from '@extensions/expense/tabs/expense-signoff/expense-signoff.component';
import { ExpenseComponent } from '@extensions/expense/tabs/expense/expense.component';
import { AssignedWorkComponent } from '@extensions/assignment/tabs/assigned-work/assigned-work.component';
import { InfoProjectComponent } from './info-project/info-project.component';
import { InfoPortfolioComponent } from './info-portfolio/info-portfolio.component';
import { PortfolioService } from '@shared/services/portfolio.service';
import { IProjectWBS } from '@static/models/projwbs';
import { ChartsComponent } from '@extensions/charts/charts.component';
import { ProjectSettingsPageComponent } from './project-settings/project-settings-page.component';

@Component({
  selector: 'app-activities',
  imports: [
    LockComponent,
    TaskListComponent,
    AssignedWorkComponent,
    ContractorListComponent,
    ProjectPermissionsComponent,
    ExpenseComponent,
    ExpenseSignoffComponent,
    ReviewComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatInputModule,
    MatCardModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ReactiveFormsModule,
    InfoProjectComponent,
    InfoPortfolioComponent,
    ChartsComponent,
    ProjectSettingsPageComponent
  ],
  templateUrl: './activities.component.html',
  styleUrl: './activities.component.css',
  providers: [
    ObservableService,
  ]
})
export class ActivitiesComponent implements OnInit, OnDestroy {
  private _observableService: ObservableService = inject(ObservableService);
  private _storageService: StorageService = inject(StorageService);
  private _router: Router = inject(Router);
  protected _projectService: ProjectService = inject(ProjectService);
  private _authService: AuthService = inject(AuthService);
  protected _activitiesService: ActivitiesService = inject(ActivitiesService);
  private _wbsFilterService: WbsFilterService = inject(WbsFilterService);
  private _workService: WorkService = inject(WorkService);
  private _extensionService: ExtensionService = inject(ExtensionService);
  private _portfolioService: PortfolioService = inject(PortfolioService);

  // Optional project id input parameter
  @Input() id?: string;

  public projReqError?: string;
  public staffReqError?: string;

  public selectedTab: number;
  public selectedProjectInfo: IProject[];

  protected projectWBS?: IProjectWBS[];
  protected selectedName: string;

  public accessControl: boolean;
  public accessLevel: Signal<number>;
  public isSiteAdmin: boolean;
  public isGlobalAdmin: boolean;

  protected lockedState: boolean;
  protected allLocked: boolean;
  protected isArchived: Signal<boolean>;
  protected isPortfolio: boolean;
  protected hasNotificationsOn: boolean;
  protected hasProjectOwner: boolean;

  protected extAssignment: boolean;
  protected extExpense: boolean;
  protected extChanges: boolean;
  protected extCharts: boolean;
  protected canLock: boolean;
  protected showSettings: boolean;

  public PERMISSION_LEVEL = EPermissionLevel;

  constructor() {
    this.selectedTab = 0;
    this.accessControl = false;
    this.accessLevel = signal(EPermissionLevel.NONE);
    this.isSiteAdmin = this._authService.isSiteAdmin;
    this.isGlobalAdmin = this._authService.isGlobalAdmin;

    this.extAssignment = false;
    this.extExpense = false;
    this.extChanges = false;
    this.extCharts = false;
    this.lockedState = false;
    this.allLocked = false;
    this.isArchived = signal(false);
    this.isPortfolio = false;
    this.selectedProjectInfo = [];
    this.selectedName = '';
    this.hasNotificationsOn = false;
    this.hasProjectOwner = false;
    this.canLock = false;
    this.showSettings = false;
  }

  /**
   * Implement project information load on init to allow for access to the
   * optional fixed proj id parameter
   */
  ngOnInit(): void {
    // Check for parameter project
    if (this.id) {
      const isPortfolio = this.id.startsWith('p');
      this._projectService.isPortfolio.set(isPortfolio);
      this._projectService.currentId = this.id;
    }

    // Check for loaded project
    const projId = this._projectService.currentId;
    if (!projId) {
      this._router.navigate(['/projects']);
      return;
    };

    // Initiate loading of RBAC rules for notify customer button
    if (this.isSiteAdmin) this._projectService.getRBACRules();
    this.lockedState = this._projectService.isProjectLockedForEdit(projId);
    this._observableService.subscribe(
      this._projectService.projectLockUpdated,
      () => {
        this.lockedState = this._projectService.isProjectLockedForEdit(projId);
        this.allLocked = this.selectedProjectInfo.every(p => p.lock === 1);
      }
    );

    // Load the filter state for the current project
    this._wbsFilterService.loadSavedFilters();

    // Get detailed project information
    this._projectService.getCurrentInfo().then(() => {
      const pId = this._projectService.currentId;
      this.accessControl = this._projectService.getProjectRBACEnabled(pId);
      this.accessLevel = this._projectService.getProjectAccessLevel(pId);
      this.updateNotificationState();
      this.updateExtensionState();
      this.selectedProjectInfo = this._projectService.getSelectedProjectInfo();
      this.allLocked = this.selectedProjectInfo.every(p => p.lock === 1);
      this.isPortfolio = this._projectService.isPortfolio();
      this.showSettings = this.isSiteAdmin && !this.isPortfolio;
      this.isArchived = this._projectService.isProjectArchived(pId);

      const ownerEmail = pId ? this._projectService.getProjectSettings(pId)?.kpa_contact_email : null;
      this.hasProjectOwner = !!ownerEmail?.trim();

      // Get the WBS structure
      this._activitiesService.getWBSTaskStructure();

      // Set the initial tab based on any stored value
      const data = this._storageService.loadData(KEY_PROJECT_CURRENT_TAB);
      if (data) this.selectedTab = +data;
    }).catch(() => {
      this._router.navigate(['/projects']);
      return;
    });

    // Listen for WBS changes
    this._observableService.subscribe(
      this._activitiesService.wbsUpdate,
      () => {
        this.projectWBS = this._activitiesService.currentWBS;
        if (this._projectService.isPortfolio()) {
          this.selectedName = this._projectService.selectedPortfolioName ?? '';
        } else {
          this.selectedName = `${this.projectWBS?.at(0)?.wbs_name} (${this.selectedProjectInfo[0].proj_short_name})`;
        }
      }
    );

    // Listen for changes in the selected portfolio
    this._observableService.subscribe(
      this._portfolioService.portfoliosUpdated,
      () => {
        this.selectedName = this._projectService.selectedPortfolioName ?? '';
      }
    );

    // Listen for changes to rbac rules
    this._observableService.subscribe(
      this._projectService.rbacRulesUpdated,
      () => this.updateNotificationState()
    );

    // Listen for changes to list of extensions in settings
    this._observableService.subscribe(
      this._extensionService.extensionsUpdate,
      () => this.updateExtensionState()
    );

    // Listen for changes to rbac enabled state
    this._observableService.subscribe(
      this._projectService.rbacRulesUpdated,
      () => this.accessControl = this._projectService.getProjectRBACEnabled(this._projectService.currentId)
    )
  }

  /**
   * Track and record updates in the currently selected tab
   *
   * @param tabChangeEvent  tab change event
   */
  public tabChange(tabChangeEvent: MatTabChangeEvent): void {
    this.selectedTab = tabChangeEvent.index;
    this._storageService.saveData(KEY_PROJECT_CURRENT_TAB, this.selectedTab.toString());
  }

  /**
   * Update state of customer notifications for the project
   */
  public updateNotificationState(): void {
    const pId = this._projectService.currentId;
    if (pId) this.hasNotificationsOn = this._projectService.checkNotifyCustomer(pId);
  }

  /**
   * Update extension state in constructor or when extensions change in settings tab
   */
  private updateExtensionState(): void {
    const pId = this._projectService.currentId;

    this.extAssignment = pId ? this._extensionService.checkCurrentExtensionEnabled(pId, 'work_assignment') : false;
    this.extExpense = pId ? this._extensionService.checkCurrentExtensionEnabled(pId, 'project_expenses') : false;
    this.extChanges = pId ? this._extensionService.checkCurrentExtensionEnabled(pId, 'wbs_change') : false;
    this.extCharts = pId ? this._extensionService.checkCurrentExtensionEnabled(pId, 'charts') : false;
    this.canLock = this.extAssignment || this.extExpense || this.extChanges;
  }


  /**
   * On destroy actions
   */
  ngOnDestroy(): void {
    this._activitiesService.clearProject();
    this._projectService.clearCurrent();
    this._workService.clearProjectWork();
    this._wbsFilterService.clearProject();
  }

}
